import React, { lazy, Suspense, useContext, useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";
import Logo from "images/logo_menu.svg";

import MenuIcon from "@mui/icons-material/Menu";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
    Slide,
    Dialog,
    AppBar,
    Toolbar,
    IconButton,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import { SearchResultsButton } from "./components/results-button";
import { categoriesItems } from "./utils/categoriesItems";
import { SearchCategory } from "./components/category";
import { SearchFormContext } from "./search-form-core";
import useId from "@mui/utils/useId";
import { ClearFormButton } from "./components/clear-form-button";
import { useFormContext } from "react-hook-form";
import loadable from "@loadable/component";
const SearchGlobe = loadable(() => import("./components/globe"));

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement;
    },
    ref: React.Ref<unknown>
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

type SearchFormMobileProps = {
    open: boolean;
    category?: string;
    handleClose: () => void;
};

export const SearchFormMobile = ({
    open,
    category,
    handleClose
}: SearchFormMobileProps) => {
    const [expanded, setExpanded] = React.useState<string | false>(
        category || "essentials"
    );
    const id = useId();
    const [menuOpen, setMenuOpen] = React.useState(false);
    const form = useFormContext();
    const clearForm = () => {
        form.reset();
    };
    useEffect(() => {
        if (open) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "auto";
        }
        return () => {
            document.body.style.overflow = "auto";
        };
    }, [open]);
    useEffect(() => {
        if (!expanded) {
            return;
        }
        const summaryNode = document.getElementById(`${id}-${expanded}-header`);
        if (summaryNode) {
            setTimeout(() => {
                summaryNode.scrollIntoView({
                    block: "nearest",
                    behavior: "smooth"
                });
            }, 500);
        }
    }, [expanded]);
    const handleChange =
        (panel: string) =>
        (event: React.SyntheticEvent, isExpanded: boolean) => {
            setExpanded(isExpanded ? panel : false);
        };
    const { categories: categoriesCounts } = useContext(SearchFormContext);
    const handleMenuClose = () => {
        setMenuOpen(false);
    };
    return (
        <Dialog
            fullScreen
            disableScrollLock
            open={open}
            onClose={() => handleClose()}
            TransitionComponent={Transition}
            container={() => document.getElementById("___gatsby")}
        >
            <div className="flex h-[100dvh] flex-col">
                <AppBar
                    className="z-[1] bg-white "
                    position="static"
                    sx={{
                        boxShadow: "0px 2px 5px -1px rgba(0, 0, 0, 0.2)"
                    }}
                >
                    <Toolbar>
                        <IconButton
                            className="justify-start"
                            color="shBlack"
                            onClick={handleClose}
                            aria-label="close"
                        >
                            <CloseIcon />
                        </IconButton>
                        <div className="flex flex-1 items-center justify-center gap-[16px] text-center text-zinc-800">
                            <img
                                src={Logo}
                                alt="Sunheron logo"
                                className="h-[24px] w-[24px]"
                            />
                            <div>Sunheron search</div>
                        </div>
                        <IconButton
                            color="shBlack"
                            onClick={() => setMenuOpen(true)}
                            aria-label="close"
                        >
                            <MenuIcon />
                        </IconButton>
                    </Toolbar>
                </AppBar>
                <div className="flex-1 overflow-y-auto py-4">
                    {categoriesItems.map((item) => (
                        <Accordion
                            key={item.section}
                            className="py-[8px]"
                            disableGutters
                            elevation={0}
                            expanded={expanded === item.section}
                            onChange={handleChange(item.section)}
                            sx={{
                                "&:before": {
                                    display: "none"
                                },
                                "& .MuiPaper-root:before": {
                                    display: "none"
                                }
                            }}
                        >
                            <AccordionSummary
                                id={`${id}-${item.section}-header`}
                                expandIcon={<ExpandMoreIcon />}
                                className="mx-[16px] rounded-md bg-zinc-100 py-[4px]"
                                sx={{
                                    "& .MuiAccordionSummary-content": {
                                        paddingY: 0,
                                        marginY: 0
                                    }
                                }}
                            >
                                <SearchCategory
                                    noBackground
                                    name={item.section}
                                    isActive={expanded === item.section}
                                    count={categoriesCounts[item.section] || 0}
                                >
                                    {item.title}
                                </SearchCategory>
                            </AccordionSummary>
                            <AccordionDetails className="py-[24px] px-[32px]">
                                <item.component />
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </div>

                <AppBar
                    position="static"
                    sx={{
                        flexDirection: {
                            is: "row"
                        },
                        boxShadow: "0px -2px 5px -1px rgba(0, 0, 0, 0.2)"
                    }}
                    className="z-[1] flex items-stretch justify-center bg-white pb-[8px] xs:items-center"
                >
                    <div className="px-[16px] xsmax:mx-auto">
                        <SearchGlobe isSmall />
                    </div>
                    <div className="px-[16px]">
                        <SearchResultsButton />
                    </div>
                </AppBar>
            </div>
            <Dialog
                fullScreen
                disableScrollLock
                open={menuOpen}
                onClose={handleMenuClose}
                TransitionComponent={Transition}
                container={() => document.getElementById("___gatsby")}
            >
                <AppBar
                    className="z-[1] bg-white "
                    position="static"
                    sx={{
                        boxShadow: "0px 2px 5px -1px rgba(0, 0, 0, 0.2)"
                    }}
                >
                    <Toolbar>
                        <div className="h-[40px] w-[40px]" />
                        <div className="flex-1 text-center text-zinc-800">
                            Search sections
                        </div>
                        <IconButton
                            color="shBlack"
                            onClick={handleMenuClose}
                            aria-label="close"
                        >
                            <CloseIcon />
                        </IconButton>
                    </Toolbar>
                </AppBar>
                <div className="p-[16px]">
                    {categoriesItems.map((item) => (
                        <div className="py-[4px]" key={item.section}>
                            <SearchCategory
                                noBackground
                                name={item.section}
                                isActive={expanded === item.section}
                                count={0}
                                onClick={() => {
                                    setExpanded(item.section);
                                    setMenuOpen(false);
                                }}
                            >
                                {item.title}
                            </SearchCategory>
                        </div>
                    ))}
                    <ClearFormButton
                        onClick={() => {
                            clearForm();
                            setExpanded("essentials");
                            setMenuOpen(false);
                        }}
                        className="mt-8 w-full"
                    />
                </div>
            </Dialog>
        </Dialog>
    );
};
